import { FormEvent, ReactNode, useState } from 'react'
import {
    UpdateAffiliateBody,
    useAdminRateHistory,
    useCreateAdjustment,
    useFinalPayout,
    useUpdateAffiliate,
} from 'src/hooks/useAdminAffiliates'
import { AdminAffiliateDetail, AffiliateStatus, RateHistoryEntry } from 'src/types/Affiliate'
import { dollarsToCents, formatBps, formatCents, formatDay } from 'src/utils/affiliateFormat'
import { notify } from 'src/utils/notify'
import { errorMessage } from '../affiliate/labels'
import {
    Badge,
    Banner,
    Card,
    CardTitle,
    DangerButton,
    EmptyRow,
    ErrorText,
    Grid,
    InlineSpinner,
    Input,
    Label,
    Muted,
    PrimaryButton,
    Row,
    SecondaryButton,
    Segmented,
    Select,
    Table,
    TableWrap,
    TextArea,
    Tile,
} from '../affiliate/ui'
import { CODE_PATTERN, FieldGroup, FormError, Mono, formMessage, readDollars, readPercent } from './shared'

type Props = { affiliate: AdminAffiliateDetail }

/* --------------------------------- Balance --------------------------------- */

export const BalanceTiles = ({ affiliate }: Props) => {
    const { balance } = affiliate
    return (
        <Grid $min={160}>
            <Tile label="Pending" value={formatCents(balance.pendingCents)} />
            <Tile
                label="Available"
                value={formatCents(balance.availableCents)}
                negative={balance.availableCents < 0}
                hint={balance.availableCents < 0 ? 'Owed; offset by future earnings' : undefined}
            />
            <Tile label="In flight" value={formatCents(balance.inFlightCents)} />
            <Tile label="Paid" value={formatCents(balance.paidCents)} />
            <Tile label="Lifetime" value={formatCents(balance.lifetimeCents)} />
        </Grid>
    )
}

/* ---------------------------------- Rates ---------------------------------- */

type RateField = 'firstMonthBps' | 'recurringBps' | 'minPayoutCents'

const RATE_FIELDS: { field: RateField; label: string; unit: '%' | '$' }[] = [
    { field: 'firstMonthBps', label: 'First month', unit: '%' },
    { field: 'recurringBps', label: 'Recurring', unit: '%' },
    { field: 'minPayoutCents', label: 'Payout threshold', unit: '$' },
]

const formatRateValue = (field: RateField, value: number | null | undefined) => {
    if (value == null) return 'default'
    return field === 'minPayoutCents' ? formatCents(value) : formatBps(value)
}

const toInput = (field: RateField, value: number) => String(value / 100)

type RateMode = 'default' | 'override'
type RateState = Record<RateField, { mode: RateMode; value: string }>

export const RatesCard = ({ affiliate }: Props) => {
    const update = useUpdateAffiliate(affiliate.id)
    const [state, setState] = useState<RateState>(() => {
        const initial = {} as RateState
        RATE_FIELDS.forEach(({ field }) => {
            const override = affiliate.overrides[field]
            initial[field] = {
                mode: override == null ? 'default' : 'override',
                value: toInput(field, override == null ? affiliate.rates[field] : override),
            }
        })
        return initial
    })

    const setField = (field: RateField, patch: Partial<RateState[RateField]>) =>
        setState((current) => ({ ...current, [field]: { ...current[field], ...patch } }))

    const submit = (e: FormEvent) => {
        e.preventDefault()
        const body: UpdateAffiliateBody = {}
        try {
            RATE_FIELDS.forEach(({ field, label, unit }) => {
                const { mode, value } = state[field]
                let next: number | null = null
                if (mode === 'override') {
                    const parsed = unit === '%' ? readPercent(value, label) : readDollars(value, label)
                    if (parsed === undefined) throw new FormError(`Enter a value for ${label} or use the default.`)
                    next = parsed
                }
                if (next !== (affiliate.overrides[field] ?? null)) body[field] = next
            })
        } catch (err) {
            notify(formMessage(err) || 'Check the form.', 'error')
            return
        }
        if (Object.keys(body).length === 0) {
            notify('Nothing changed.', 'info')
            return
        }
        update.mutate(body, {
            onSuccess: () => notify('Rates updated.', 'success'),
            onError: (err) => notify(errorMessage(err), 'error'),
        })
    }

    return (
        <Card as="form" onSubmit={submit}>
            <CardTitle>Rates &amp; overrides</CardTitle>
            <Muted>Changes apply to invoices paid from now on. Existing commissions keep their rate.</Muted>
            <Grid $min={220}>
                {RATE_FIELDS.map(({ field, label, unit }) => {
                    const override = affiliate.overrides[field]
                    return (
                        <FieldGroup key={field}>
                            <Row>
                                <strong>{label}</strong>
                                <Badge $tone={override == null ? 'neutral' : 'pink'}>
                                    {override == null ? 'Default' : 'Override'}
                                </Badge>
                            </Row>
                            <Muted>Effective: {formatRateValue(field, affiliate.rates[field])}</Muted>
                            <Segmented
                                options={[
                                    { value: 'default', label: 'Use default' },
                                    { value: 'override', label: 'Override' },
                                ]}
                                value={state[field].mode}
                                onChange={(mode) => setField(field, { mode })}
                            />
                            {state[field].mode === 'override' && (
                                <Label>
                                    {unit === '%' ? 'Percent' : 'Dollars'}
                                    <Input
                                        inputMode="decimal"
                                        value={state[field].value}
                                        onChange={(e) => setField(field, { value: e.target.value })}
                                    />
                                </Label>
                            )}
                        </FieldGroup>
                    )
                })}
            </Grid>
            <Row>
                <PrimaryButton type="submit" disabled={update.isPending}>
                    {update.isPending ? 'Saving…' : 'Save rates'}
                </PrimaryButton>
            </Row>
        </Card>
    )
}

/* ------------------------------- Rate history ------------------------------ */

type Overrides = AdminAffiliateDetail['overrides']

const describeChange = (before: Overrides, after: Overrides) =>
    RATE_FIELDS.filter(({ field }) => (before[field] ?? null) !== (after[field] ?? null)).map(
        ({ field, label }) => `${label}: ${formatRateValue(field, before[field])} → ${formatRateValue(field, after[field])}`
    )

export const RateHistoryCard = ({ affiliate }: Props) => {
    const { data, isLoading, error } = useAdminRateHistory(affiliate.id)

    // Entries are oldest first and hold the values from before each change.
    const entries = (data || []).map((entry: RateHistoryEntry, index: number, all: RateHistoryEntry[]) => {
        const after = index + 1 < all.length ? all[index + 1] : affiliate.overrides
        return { entry, changes: describeChange(entry, after) }
    })

    return (
        <Card>
            <CardTitle>Rate history</CardTitle>
            {isLoading ? (
                <InlineSpinner />
            ) : error ? (
                <ErrorText>{errorMessage(error)}</ErrorText>
            ) : entries.length === 0 ? (
                <Muted>No rate changes.</Muted>
            ) : (
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 12, listStyle: 'none' }}>
                    {entries
                        .slice()
                        .reverse()
                        .map(({ entry, changes }, index) => (
                            <li key={index}>
                                <Muted>
                                    {new Date(entry.changedAt).toLocaleString()}
                                    {entry.changedBy &&
                                        `, by ${entry.changedBy.discordUsername ? `@${entry.changedBy.discordUsername}` : entry.changedBy.id}`}
                                </Muted>
                                {changes.length > 0 ? (
                                    changes.map((line) => <p key={line}>{line}</p>)
                                ) : (
                                    <p>
                                        Before: {RATE_FIELDS.map(({ field, label }) => `${label} ${formatRateValue(field, entry[field])}`).join(', ')}
                                    </p>
                                )}
                            </li>
                        ))}
                </ul>
            )}
        </Card>
    )
}

/* --------------------------------- Status ---------------------------------- */

const STATUS_ACTIONS: Record<AffiliateStatus, { status: AffiliateStatus; label: string }[]> = {
    applied: [
        { status: 'active', label: 'Approve' },
        { status: 'rejected', label: 'Reject' },
    ],
    active: [{ status: 'paused', label: 'Pause' }],
    paused: [{ status: 'active', label: 'Resume' }],
    rejected: [{ status: 'active', label: 'Activate' }],
    terminated: [],
}

export const StatusCard = ({ affiliate }: Props) => {
    const update = useUpdateAffiliate(affiliate.id)
    const [reason, setReason] = useState<'other' | 'fraud'>('other')
    const canTerminate = affiliate.status !== 'terminated'

    const run = (body: UpdateAffiliateBody, message: string) =>
        update.mutate(body, {
            onSuccess: () => notify(message, 'success'),
            onError: (err) => notify(errorMessage(err), 'error'),
        })

    const changeStatus = (status: AffiliateStatus, label: string) => {
        if (!window.confirm(`${label} ${affiliate.code}?`)) return
        run({ status }, `Status changed to ${status}.`)
    }

    const terminate = () => {
        if (reason === 'fraud') {
            if (
                !window.confirm(
                    `Terminate ${affiliate.code} for FRAUD? This forfeits everything not yet paid (${formatCents(
                        affiliate.balance.pendingCents + affiliate.balance.availableCents
                    )} pending + available) and cannot be undone.`
                )
            ) {
                return
            }
            const typed = window.prompt(`Type the affiliate code "${affiliate.code}" to confirm:`)
            if (typed === null) return
            if (typed.trim().toLowerCase() !== affiliate.code) {
                notify('Code did not match. Nothing changed.', 'error')
                return
            }
        } else if (
            !window.confirm(`Terminate ${affiliate.code}? They keep their unpaid balance and can be paid a final payout.`)
        ) {
            return
        }
        run({ status: 'terminated', terminationReason: reason }, 'Affiliate terminated.')
    }

    return (
        <Card>
            <CardTitle>Status</CardTitle>
            {affiliate.status === 'terminated' && (
                <Muted>Terminated{affiliate.terminationReason ? ` (${affiliate.terminationReason})` : ''}.</Muted>
            )}
            <Row>
                {STATUS_ACTIONS[affiliate.status].map(({ status, label }) =>
                    status === 'rejected' || status === 'paused' ? (
                        <SecondaryButton key={status} disabled={update.isPending} onClick={() => changeStatus(status, label)}>
                            {label}
                        </SecondaryButton>
                    ) : (
                        <PrimaryButton key={status} disabled={update.isPending} onClick={() => changeStatus(status, label)}>
                            {label}
                        </PrimaryButton>
                    )
                )}
            </Row>
            {canTerminate && (
                <FieldGroup>
                    <Label>
                        Termination reason
                        <Select value={reason} onChange={(e) => setReason(e.target.value as 'other' | 'fraud')}>
                            <option value="other">Other (keeps unpaid balance)</option>
                            <option value="fraud">Fraud (forfeits unpaid balance)</option>
                        </Select>
                    </Label>
                    {reason === 'fraud' && (
                        <Banner $tone="error">
                            <p>Fraud termination forfeits every commission that has not been paid yet.</p>
                        </Banner>
                    )}
                    <Row>
                        <DangerButton disabled={update.isPending} onClick={terminate}>
                            Terminate
                        </DangerButton>
                    </Row>
                </FieldGroup>
            )}
        </Card>
    )
}

/* ------------------------------ Code and notes ----------------------------- */

export const CodeCard = ({ affiliate }: Props) => {
    const update = useUpdateAffiliate(affiliate.id)
    const [code, setCode] = useState(affiliate.code)
    const normalized = code.trim().toLowerCase()

    const submit = (e: FormEvent) => {
        e.preventDefault()
        if (!CODE_PATTERN.test(normalized)) {
            notify('Codes are 3–20 characters: lowercase letters, numbers and dashes.', 'error')
            return
        }
        if (!window.confirm(`Change the code to "${normalized}"? Links using "${affiliate.code}" will stop working.`)) return
        update.mutate(
            { code: normalized },
            {
                onSuccess: () => notify('Code updated.', 'success'),
                onError: (err) => notify(errorMessage(err), 'error'),
            }
        )
    }

    return (
        <Card as="form" onSubmit={submit}>
            <CardTitle>Vanity code</CardTitle>
            <Label>
                Code
                <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={20} />
            </Label>
            <Row>
                <PrimaryButton type="submit" disabled={update.isPending || normalized === affiliate.code}>
                    Save code
                </PrimaryButton>
            </Row>
        </Card>
    )
}

export const NotesCard = ({ affiliate }: Props) => {
    const update = useUpdateAffiliate(affiliate.id)
    const [notes, setNotes] = useState(affiliate.notes)

    const save = () =>
        update.mutate(
            { notes },
            {
                onSuccess: () => notify('Notes saved.', 'success'),
                onError: (err) => notify(errorMessage(err), 'error'),
            }
        )

    return (
        <Card>
            <CardTitle>Notes</CardTitle>
            <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={5000} placeholder="Internal notes" />
            <Row>
                <PrimaryButton disabled={update.isPending || notes === affiliate.notes} onClick={save}>
                    Save notes
                </PrimaryButton>
            </Row>
        </Card>
    )
}

/* ------------------------------ Terms, Connect ----------------------------- */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
    <FieldGroup>
        <Muted>{label}</Muted>
        <div style={{ overflowWrap: 'anywhere' }}>{children}</div>
    </FieldGroup>
)

const CODE_KIND = { primary: 'Main', campaign: 'Campaign', retired: 'Retired' } as const

/** Every code the affiliate has, with its trial setting. Codes are managed by the affiliate. */
export const CodesCard = ({ affiliate }: Props) => (
    <Card>
        <CardTitle>Codes</CardTitle>
        <TableWrap>
            <Table>
                <thead>
                    <tr>
                        <th>Code</th>
                        <th>Kind</th>
                        <th>Label</th>
                        <th>Free trial</th>
                        <th>Retired</th>
                    </tr>
                </thead>
                <tbody>
                    {affiliate.codes.length > 0 ? (
                        affiliate.codes.map((code) => (
                            <tr key={code.code}>
                                <td>
                                    <strong>{code.code}</strong>
                                </td>
                                <td>
                                    <Badge $tone={code.kind === 'primary' ? 'pink' : code.kind === 'retired' ? 'neutral' : 'blue'}>
                                        {CODE_KIND[code.kind]}
                                    </Badge>
                                </td>
                                <td>{code.label || '—'}</td>
                                <td>{code.trial ? `${code.trialDays} days` : '—'}</td>
                                <td>{code.retiredAt ? formatDay(code.retiredAt) : '—'}</td>
                            </tr>
                        ))
                    ) : (
                        <EmptyRow colSpan={5}>No codes.</EmptyRow>
                    )}
                </tbody>
            </Table>
        </TableWrap>
    </Card>
)

export const TermsCard = ({ affiliate }: Props) => {
    const { terms } = affiliate
    return (
        <Card>
            <Row>
                <CardTitle>Terms</CardTitle>
                {terms.needsAcceptance ? <Badge $tone="yellow">Needs acceptance</Badge> : <Badge $tone="green">Up to date</Badge>}
            </Row>
            <Grid $min={150}>
                <Field label="Accepted version">{terms.acceptedVersion || '—'}</Field>
                <Field label="Accepted at">{formatDay(terms.acceptedAt)}</Field>
                <Field label="Current version">{terms.currentVersion}</Field>
            </Grid>
        </Card>
    )
}

const connectTone = { not_started: 'neutral', incomplete: 'yellow', restricted: 'red', enabled: 'green' } as const

export const ConnectCard = ({ affiliate }: Props) => {
    const { connect } = affiliate
    return (
        <Card>
            <Row>
                <CardTitle>Stripe Connect</CardTitle>
                <Badge $tone={connectTone[connect.status]}>{connect.status.replace('_', ' ')}</Badge>
            </Row>
            <Grid $min={150}>
                <Field label="Payouts enabled">{connect.payoutsEnabled ? 'Yes' : 'No'}</Field>
                <Field label="Account">{connect.stripeAccountId ? <Mono>{connect.stripeAccountId}</Mono> : '—'}</Field>
                <Field label="Disabled reason">{connect.disabledReason || '—'}</Field>
                <Field label="Requirements due">
                    {connect.requirementsDue.length > 0 ? connect.requirementsDue.join(', ') : 'None'}
                </Field>
                <Field label="Last bank payout">
                    {connect.lastBankPayout ? (
                        <>
                            {formatCents(connect.lastBankPayout.amountCents)}{' '}
                            <Badge $tone={connect.lastBankPayout.status === 'paid' ? 'green' : 'red'}>
                                {connect.lastBankPayout.status}
                            </Badge>{' '}
                            {formatDay(connect.lastBankPayout.arrivalDate)}
                            {connect.lastBankPayout.failureMessage && (
                                <ErrorText>{connect.lastBankPayout.failureMessage}</ErrorText>
                            )}
                        </>
                    ) : (
                        '—'
                    )}
                </Field>
            </Grid>
        </Card>
    )
}

/* -------------------------- Adjustment, final payout ----------------------- */

export const AdjustmentCard = ({ affiliate }: Props) => {
    const create = useCreateAdjustment(affiliate.id)
    const [amount, setAmount] = useState('')
    const [note, setNote] = useState('')

    const submit = (e: FormEvent) => {
        e.preventDefault()
        const cents = dollarsToCents(amount)
        if (!Number.isFinite(cents) || !cents) {
            notify('Enter a non-zero dollar amount (negative for a correction).', 'error')
            return
        }
        if (!note.trim()) {
            notify('A note is required.', 'error')
            return
        }
        if (!window.confirm(`${cents < 0 ? 'Deduct' : 'Credit'} ${formatCents(Math.abs(cents))} ${cents < 0 ? 'from' : 'to'} ${affiliate.code}? It is available immediately.`)) {
            return
        }
        create.mutate(
            { amountCents: cents, note: note.trim() },
            {
                onSuccess: () => {
                    notify('Adjustment added.', 'success')
                    setAmount('')
                    setNote('')
                },
                onError: (err) => notify(errorMessage(err), 'error'),
            }
        )
    }

    return (
        <Card as="form" onSubmit={submit}>
            <CardTitle>Adjustment</CardTitle>
            <Muted>Credits (or, if negative, deducts) the available balance immediately.</Muted>
            <Grid $min={180}>
                <Label>
                    Amount $
                    <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="25 or -10" />
                </Label>
                <Label>
                    Note (required)
                    <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
                </Label>
            </Grid>
            <Row>
                <PrimaryButton type="submit" disabled={create.isPending || !amount.trim() || !note.trim()}>
                    {create.isPending ? 'Adding…' : 'Add adjustment'}
                </PrimaryButton>
            </Row>
        </Card>
    )
}

export const FinalPayoutCard = ({ affiliate }: Props) => {
    const finalPayout = useFinalPayout(affiliate.id)
    const connectReady = affiliate.connect.status === 'enabled'
    const amount = affiliate.balance.availableCents

    const run = () => {
        if (!window.confirm(`Pay out the available balance of ${affiliate.code} (${formatCents(amount)}), ignoring the threshold?`)) return
        finalPayout.mutate(undefined, {
            onSuccess: (payout) => notify(`Final payout of ${formatCents(payout.amountCents)} started.`, 'success'),
            onError: (err) => notify(errorMessage(err), 'error'),
        })
    }

    return (
        <Card>
            <CardTitle>Final payout</CardTitle>
            <Muted>
                Pays the available balance of this terminated affiliate ({formatCents(amount)}), ignoring the payout
                threshold. Commissions still in the hold window or under review are not included; run it again once
                they mature.
            </Muted>
            {!connectReady && <ErrorText>Stripe Connect must be enabled before a final payout.</ErrorText>}
            <Row>
                <DangerButton disabled={finalPayout.isPending || !connectReady} onClick={run}>
                    {finalPayout.isPending ? 'Starting…' : 'Send final payout'}
                </DangerButton>
            </Row>
        </Card>
    )
}
